import {
    CompanyReferralCodeSelector,
    CompanyOldClientIdSelector,
    CompanySecondaryEmailSelector
} from './../../../../../../assets/descriptors/panel-clients/company/company-form.descriptor';
import { FormControl, FormGroup } from '@angular/forms';

import { map, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

import {
    ApiError,
    ApiResponse,
    EnrollChannelsEnum,
    HttpStatusCodesEnum,
    CompaniesService,
    EnrollmentsService,
    EnrollmentCompanyInterface,
    CountryModel,
    CompanyModel,
    CompanyExtendedModel,
    EnrollmentCompanyModel,
    EnrollmentCompanyInputModel
} from '@spa/api';

import { InArrayValidator } from '@spa/validators';

import { Helper, RadioGroupControl } from '../../../../../shared/classes';
import { RadioGroupOptionInterface } from '../../../../../shared/components';
import { BaseControl, CountryAutocompleteControl } from '../../../../../shared/classes/controls';

import { CompanyFormErrorStatesEnum, FinancialInstitutionEnum } from '../../../enums';

import {
    CompanyDetailsControls,
    CompanyEmployeeControls,
    CompanyDetailsNameSelector,
    CompanyMiFIDCategorySelector,
    CompanyDetailsTradingCategorySelector,
    CompanyDetailsOrganizationTypeSelector,
    CompanyEmployeeDetailsLastNameSelector,
    CompanyEmployeeDetailsFirstNameSelector,
    CompanyEmployeeDetailsMiddleNameSelector,
    CompanyDetailsDateOfIncorporationSelector,
    CompanyDetailsFinancialInstitutionSelector,
    CompanyDetailsCountryOfIncorporationSelector
} from '../../../../../../assets/descriptors';
import { EventEmitter } from '@angular/core';

export class CompanyForm {
    private readonly userId: number;
    private readonly companiesService: CompaniesService;
    private readonly enrollmentsService: EnrollmentsService;

    private company: CompanyExtendedModel;

    public companyForm: FormGroup;
    public companyEmployeeForm: FormGroup;

    public companyFormControls: BaseControl[];
    public companyEmployeeFormControls: BaseControl[];

    public companyFormControlsObservable: BehaviorSubject<BaseControl[]>;
    public companyEmployeeFormControlsObservable: BehaviorSubject<BaseControl[]>;
    public oldClientIdUpdatedEvent = new EventEmitter<string>();

    constructor(
        userId: number,
        company: CompanyExtendedModel,
        companiesService: CompaniesService,
        enrollmentsService: EnrollmentsService
    ) {
        this.userId = userId;
        this.company = company;
        this.companiesService = companiesService;
        this.enrollmentsService = enrollmentsService;
    }

    private initCompanyFormControls(countries: CountryModel[]): BaseControl[] {
        return CompanyDetailsControls().map((control: BaseControl) => {
            switch (control.name) {
                case CompanyDetailsNameSelector:
                    control.value = !this.company.name ? '' : this.company.name;
                    break;
                case CompanyDetailsOrganizationTypeSelector:
                    control.value = !this.company.organizationType ? null : this.company.organizationType;
                    break;
                case CompanyDetailsDateOfIncorporationSelector:
                    control.value = !this.company.dateOfIncorporation ? null : this.company.dateOfIncorporation;
                    break;
                case CompanyDetailsCountryOfIncorporationSelector:
                    control.value = !this.company.countryOfIncorporation ? null : this.company.countryOfIncorporation;

                    (control as CountryAutocompleteControl).countries = countries;

                    const countryIds = countries.map((country: CountryModel) => country.id);

                    control.extendValidators(InArrayValidator(countryIds));
                    break;
                case CompanyMiFIDCategorySelector:
                    control.value = !this.company.miFidCategory ? null : this.company.miFidCategory;

                    if (control.value) {
                        (control as RadioGroupControl).options.map((option: RadioGroupOptionInterface) => {
                            option.checked = option.value === control.value;
                        });
                    }

                    break;
                case CompanyDetailsFinancialInstitutionSelector:
                    control.value = !this.company.id ? control.value :
                        (!this.company.financialInstitution ? FinancialInstitutionEnum.No : FinancialInstitutionEnum.Yes);


                    if (control.value) {
                        (control as RadioGroupControl).options.map((option: RadioGroupOptionInterface) => {
                            option.checked = option.value === control.value;
                        });
                    }

                    break;
                case CompanyDetailsTradingCategorySelector:
                    control.value = !this.company.tradingCategory ? null : this.company.tradingCategory;
                    break;
                case CompanySecondaryEmailSelector:
                    control.value = !this.company.secondaryEmail ? null : this.company.secondaryEmail;
                    break;
                case CompanyReferralCodeSelector:
                    control.value = !this.company.referralCode ? '' : this.company.referralCode;
                    break;
                    case CompanyOldClientIdSelector:
                        control.value = !this.company.oldClientId ? '' : this.company.oldClientId;
                        break;
            }

            return control;
        });
    }

    private registerCompany(): Observable<ApiResponse<ApiError[] | boolean>> {
        const inputModel = new EnrollmentCompanyInputModel();

        inputModel.companyName = this.companyForm.value[CompanyDetailsNameSelector];
        inputModel.organizationType = this.companyForm.value[CompanyDetailsOrganizationTypeSelector];
        inputModel.dateOfIncorporation = this.companyForm.value[CompanyDetailsDateOfIncorporationSelector];
        inputModel.countryOfIncorporationId = this.companyForm.value[CompanyDetailsCountryOfIncorporationSelector];

        inputModel.financialInstitution =
            this.companyForm.value[CompanyDetailsFinancialInstitutionSelector] !== FinancialInstitutionEnum.No;

        inputModel.miFIDCategory = this.companyForm.value[CompanyMiFIDCategorySelector];
        inputModel.tradingCategory = this.companyForm.value[CompanyDetailsTradingCategorySelector];
        inputModel.enrollChanel = EnrollChannelsEnum.Offline;
        inputModel.referralCode = this.companyForm.value[CompanyReferralCodeSelector];
        inputModel.oldClientId = this.companyForm.value[CompanyOldClientIdSelector];

        inputModel.userId = this.userId;
        inputModel.firstName = this.companyEmployeeForm.value[CompanyEmployeeDetailsFirstNameSelector];
        inputModel.middleName = this.companyEmployeeForm.value[CompanyEmployeeDetailsMiddleNameSelector];
        inputModel.lastName = this.companyEmployeeForm.value[CompanyEmployeeDetailsLastNameSelector];

        return this.enrollmentsService.registerCompany(inputModel)
            .pipe(
                switchMap((response: ApiResponse<ApiError[] | EnrollmentCompanyInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<EnrollmentCompanyInterface>(
                            response,
                            CompanyFormErrorStatesEnum.CompanyRegistrationFailed
                        );
                    }

                    const data = response.data as EnrollmentCompanyInterface;
                    const model = new EnrollmentCompanyModel(data);

                    return this.getCompany(model.companyId);
                })
            );
    }

    private getCompany(id: number): Observable<ApiResponse<ApiError[] | boolean>> {
        return this.companiesService.getExtendedById(id, false)
            .pipe(
                map((response: ApiResponse<ApiError[] | CompanyExtendedModel>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            CompanyFormErrorStatesEnum.GetCompanyFailed
                        );
                    }

                    const model = response.data as CompanyExtendedModel;

                    this.company.initMembers(model.serialize());

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private checkCompanyFormForChanges(): boolean {
        return (
            this.company.name !== this.companyForm.value[CompanyDetailsNameSelector] ||
            this.company.organizationType !== this.companyForm.value[CompanyDetailsOrganizationTypeSelector] ||
            this.company.dateOfIncorporation !== this.companyForm.value[CompanyDetailsDateOfIncorporationSelector] ||
            this.company.countryOfIncorporation !== this.companyForm.value[CompanyDetailsCountryOfIncorporationSelector] ||
            this.company.miFidCategory !== this.companyForm.value[CompanyMiFIDCategorySelector] ||
            this.company.financialInstitution !== (this.companyForm.value[CompanyDetailsFinancialInstitutionSelector] !== FinancialInstitutionEnum.No) ||
            this.company.tradingCategory !== this.companyForm.value[CompanyDetailsTradingCategorySelector] ||
            this.company.referralCode !== this.companyForm.value[CompanyReferralCodeSelector] ||
            this.company.oldClientId !== this.companyForm.value[CompanyOldClientIdSelector] ||
            this.company.secondaryEmail !== Helper.getTrimmedText(this.companyForm.value[CompanySecondaryEmailSelector], true)

        );
    }

    initForm(countries: CountryModel[], skipSecondaryEmailControl: boolean = false): void {
        this.companyForm = new FormGroup({});
        this.companyEmployeeForm = new FormGroup({});

        this.companyFormControls = this.initCompanyFormControls(countries);
        if (skipSecondaryEmailControl) {
            this.companyFormControls =
                this.companyFormControls.filter((control: BaseControl) => control.name !== CompanySecondaryEmailSelector);
        }
        this.companyFormControls.forEach((control: BaseControl) => {
            this.companyForm.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.companyEmployeeFormControls = CompanyEmployeeControls().map((control: BaseControl) => {
            this.companyEmployeeForm.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.companyFormControlsObservable = new BehaviorSubject(this.companyFormControls);
        this.companyEmployeeFormControlsObservable = new BehaviorSubject(this.companyEmployeeFormControls);
    }

    validateForm(
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; },
        onlyCompanyDetailsForm: boolean = false
    ): boolean {
        let companyValidation = true;
        let companyEmployeeValidation = true;

        if (this.companyForm.invalid) {
            this.companyFormControls.map((control: BaseControl) => {
                const formControl = this.companyForm.get(control.name);

                if (formControl && formControl.invalid) {
                    control.errors = Object.keys(formControl.errors).map((key: string) => {
                        return messageFunction(control.placeholder)[key];
                    });
                }
            });

            companyValidation = false;
        }

        if (this.companyEmployeeForm.invalid) {
            this.companyEmployeeFormControls.map((control: BaseControl) => {
                const formControl = this.companyEmployeeForm.get(control.name);

                if (formControl && formControl.invalid) {
                    control.errors = Object.keys(formControl.errors).map((key: string) => {
                        return messageFunction(control.placeholder)[key];
                    });
                }
            });

            companyEmployeeValidation = false;
        }

        return !onlyCompanyDetailsForm ? companyValidation && companyEmployeeValidation : companyValidation;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        if (!this.company.id) {
            return this.registerCompany();
        }

        if (this.companyForm.dirty && this.checkCompanyFormForChanges()) {
            const companyModel = new CompanyModel(this.company.transform());

            companyModel.name = this.companyForm.value[CompanyDetailsNameSelector];
            companyModel.organizationType = this.companyForm.value[CompanyDetailsOrganizationTypeSelector];
            companyModel.dateOfIncorporation = this.companyForm.value[CompanyDetailsDateOfIncorporationSelector];
            companyModel.countryOfIncorporationId = this.companyForm.value[CompanyDetailsCountryOfIncorporationSelector];
            companyModel.miFIDCategory = this.companyForm.value[CompanyMiFIDCategorySelector];
            companyModel.financialInstitution =
                this.companyForm.value[CompanyDetailsFinancialInstitutionSelector] !== FinancialInstitutionEnum.No ;
            companyModel.tradingCategory = this.companyForm.value[CompanyDetailsTradingCategorySelector];
            companyModel.referralCode = this.companyForm.value[CompanyReferralCodeSelector];
            companyModel.oldClientId = this.companyForm.value[CompanyOldClientIdSelector];
            companyModel.secondaryEmail =  Helper.getTrimmedText(this.companyForm.value[CompanySecondaryEmailSelector], true);

            companyModel.forceAlias = false;

            return this.companiesService.update(companyModel).pipe(
                map((response: ApiResponse<ApiError[] | null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            CompanyFormErrorStatesEnum.UpdateCompanyFailed
                        );
                    }

                    this.company.name = companyModel.name;
                    this.company.organizationType = companyModel.organizationType;
                    this.company.dateOfIncorporation = companyModel.dateOfIncorporation;
                    this.company.countryOfIncorporation = companyModel.countryOfIncorporationId;
                    this.company.miFidCategory = companyModel.miFIDCategory;
                    this.company.financialInstitution = companyModel.financialInstitution;
                    this.company.tradingCategory = companyModel.tradingCategory;
                    this.company.referralCode = companyModel.referralCode;
                    this.company.secondaryEmail = companyModel.secondaryEmail;
                    if (this.company.oldClientId !== companyModel.oldClientId) {
                        this.oldClientIdUpdatedEvent.emit(companyModel.oldClientId);
                    }
                    this.company.oldClientId = companyModel.oldClientId;

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
